import React, { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, X, Send, Bot, User, Loader2, Phone, Clock, MapPin } from 'lucide-react';
import { openaiService, ChatMessage } from '@/services/openai';
import { CHATBOT_WELCOME_MESSAGE } from '@/data/chatbotKnowledge';
interface ChatbotProps {
    className?: string;
}
const Chatbot: React.FC<ChatbotProps> = ({ className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            role: 'assistant',
            content: CHATBOT_WELCOME_MESSAGE,
            timestamp: new Date()
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasLiaraConfig, setHasLiaraConfig] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        setHasLiaraConfig(openaiService.isConfigured());
    }, []);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);
    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading)
            return;
        const userMessage: ChatMessage = {
            role: 'user',
            content: inputMessage.trim(),
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);
        try {
            const response = await openaiService.sendMessage(userMessage.content);
            const assistantMessage: ChatMessage = {
                role: 'assistant',
                content: response.message,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMessage]);
            if (!response.success && hasLiaraConfig) {
                console.error('Chatbot error:', response.error);
            }
        }
        catch (error) {
            console.error('Chatbot error:', error);
            const errorMessage: ChatMessage = {
                role: 'assistant',
                content: 'متأسفانه خطایی رخ داده است. لطفاً دوباره تلاش کنید یا با شماره 021-46833010 تماس بگیرید.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    const formatTime = (date: Date) => {
        return date.toLocaleTimeString('fa-IR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    const quickActions = [
        {
            text: 'ساعات کاری',
            icon: Clock,
            message: 'ساعات کاری آزمایشگاه چیست؟'
        },
        {
            text: 'شماره تماس',
            icon: Phone,
            message: 'شماره تماس آزمایشگاه چیست؟'
        },
        {
            text: 'آدرس',
            icon: MapPin,
            message: 'آدرس آزمایشگاه کجاست؟'
        }
    ];
    const handleQuickAction = (message: string) => {
        setInputMessage(message);
    };
    return (<div className={`fixed bottom-4 left-4 z-50 ${className}`}>
      
      {!isOpen && (<Button onClick={() => setIsOpen(true)} className="h-14 w-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse" size="icon">
          <MessageCircle className="h-6 w-6 text-white"/>
        </Button>)}

      
      {isOpen && (<Card className="w-80 h-96 flex flex-col shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-lg">
            <div className="flex items-center space-x-2 space-x-reverse">
              <Bot className="h-5 w-5"/>
              <div>
                <h3 className="font-semibold text-sm">دستیار سلامت</h3>
                <p className="text-xs opacity-90">آزمایشگاه سلامت</p>
              </div>
            </div>
            <Button onClick={() => setIsOpen(false)} variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20">
              <X className="h-4 w-4"/>
            </Button>
          </div>

          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message, index) => (<div key={index} className={`flex items-start space-x-2 space-x-reverse ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {message.role === 'assistant' && (<div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-white"/>
                    </div>)}
                  <div className={`max-w-[70%] p-3 rounded-lg text-sm ${message.role === 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-br-none'
                    : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                    <p className="whitespace-pre-wrap leading-relaxed">
                      {message.content}
                    </p>
                    {message.timestamp && (<p className={`text-xs mt-1 ${message.role === 'user' ? 'text-white/70' : 'text-gray-500'}`}>
                        {formatTime(message.timestamp)}
                      </p>)}
                  </div>
                  {message.role === 'user' && (<div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-gray-600"/>
                    </div>)}
                </div>))}
              
              {isLoading && (<div className="flex items-start space-x-2 space-x-reverse justify-start">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-white"/>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg rounded-bl-none">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-500"/>
                  </div>
                </div>)}
              <div ref={messagesEndRef}/>
            </div>
          </ScrollArea>

          
          {messages.length === 1 && (<div className="px-4 pb-2">
              <div className="flex flex-wrap gap-1">
                {quickActions.map((action, index) => (<Button key={index} onClick={() => handleQuickAction(action.message)} variant="outline" size="sm" className="text-xs h-7 px-2">
                    <action.icon className="h-3 w-3 ml-1"/>
                    {action.text}
                  </Button>))}
              </div>
            </div>)}

          
          <div className="p-4 border-t">
            <div className="flex space-x-2 space-x-reverse">
              <Input ref={inputRef} value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="پیام خود را بنویسید..." className="text-right" disabled={isLoading}/>
              <Button onClick={handleSendMessage} disabled={!inputMessage.trim() || isLoading} className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" size="icon">
                {isLoading ? (<Loader2 className="h-4 w-4 animate-spin"/>) : (<Send className="h-4 w-4"/>)}
              </Button>
            </div>
            
            {!hasLiaraConfig && (<p className="text-xs text-gray-500 mt-2 text-center">
                حالت آزمایشی - پاسخ‌های محدود
              </p>)}
          </div>
        </Card>)}
    </div>);
};
export default Chatbot;
